import { get } from 'lodash-es';
import { getApiClientErrorBody, getApiClientErrorStatus } from '@/services/api/errors';
import { sentryCaptureException } from '@/utils/sentry';
import { isEmptyString } from '@/utils/string';

/**
 * The machinery shared by every Ordway credits-purchase rail: AI credits and Inbox Previews today.
 * Both endpoints start the same kind of Temporal workflow, report the same status tokens, and carry
 * the same charge-safety rules, so those live here once. A rail contributes only what differs — the
 * noun its copy and diagnostics use, and the `meta` key naming an in-flight purchase's size.
 */

/**
 * What separates one Ordway purchase rail from another. Everything else in this module — the status
 * tokens, the failure classification, the charge-safety rules — is identical across rails.
 */
export type OrdwayPurchaseRail = {
  /** Names the rail in Sentry diagnostics, e.g. `AI credits`. */
  label: string;
  /** What the copy tells a customer to check, e.g. `credit balance`. */
  balanceLabel: string;
  /** The `meta` key a 409 uses for the running purchase's size. */
  sizeMetaKey: 'quantity' | 'package';
  /** Singular noun that size counts, for copy naming a conflicting purchase. Pluralized by `plur`. */
  sizeNoun: string;
};

export const ORDWAY_AI_CREDITS_RAIL = {
  label: 'AI credits',
  balanceLabel: 'credit balance',
  sizeMetaKey: 'quantity',
  sizeNoun: 'credit pack',
} as const satisfies OrdwayPurchaseRail;

export const ORDWAY_INBOX_PREVIEWS_RAIL = {
  label: 'Inbox Previews',
  balanceLabel: 'preview balance',
  // `meta.package` is the pack's preview count, so a conflict reads "A purchase of 500 previews".
  sizeMetaKey: 'package',
  sizeNoun: 'preview',
} as const satisfies OrdwayPurchaseRail;

/**
 * The statuses an Ordway purchase workflow reports. The purchase POST resolves with `pending` or
 * `success`; the status poll adds the terminal `declined` and `failed`. The generated spec types both
 * as a bare `string`, so callers narrow through {@link parseOrdwayPurchaseStatus} rather than casting.
 */
const ORDWAY_PURCHASE_STATUSES = ['pending', 'success', 'declined', 'failed'] as const;

export type OrdwayPurchaseStatus = (typeof ORDWAY_PURCHASE_STATUSES)[number];

/** The known status matching `status`, or `undefined` when the server reported something else. */
function parseOrdwayPurchaseStatus(status: string | undefined): OrdwayPurchaseStatus | undefined {
  return ORDWAY_PURCHASE_STATUSES.find((known) => known === status);
}

/**
 * What a failed purchase establishes about the card.
 *
 * - `declined` — Ordway refused the payment, so nothing was collected and buying again is the fix.
 * - `failed` — the workflow ended in a state the status endpoint cannot tell apart from a charge. It
 *   collapses every non-declined terminal error to one token, and two of them happen *after* a
 *   successful collection: a payment that collected without applying to the invoice, and a grant that
 *   failed once the card was already charged.
 * - `indeterminate` — the poll never reached a verdict, so the charge may still be in flight.
 *
 * Only `declined` leaves buying open. The rest may have taken the customer's money, and the server's
 * duplicate guard lapses once the first workflow ends, so a second attempt would charge again.
 */
export type OrdwayPurchaseFailure = 'declined' | 'failed' | 'indeterminate';

/** A purchase failure carrying what it establishes about the charge, alongside its user-facing copy. */
export class OrdwayPurchaseError extends Error {
  readonly failure: OrdwayPurchaseFailure;

  constructor(message: string, failure: OrdwayPurchaseFailure) {
    super(message);
    this.name = 'OrdwayPurchaseError';
    this.failure = failure;
  }
}

/** What `error` establishes about the charge, or `undefined` when it is not a purchase failure. */
export function ordwayPurchaseFailure(error: unknown): OrdwayPurchaseFailure | undefined {
  return error instanceof OrdwayPurchaseError ? error.failure : undefined;
}

/** Mirrors the copy the purchase endpoint returns when the card on file is declined. */
export const ORDWAY_PURCHASE_DECLINED_MESSAGE = 'Your card was declined. Update your payment method and try again.';

/** Copy for a terminal failure whose relationship to the charge the server could not establish. */
export const ORDWAY_PURCHASE_FAILURE_MESSAGE = "Your purchase couldn't be processed. Contact support.";

/**
 * The error a terminal `failed` establishes, wherever it is read from — the workflow's status poll or
 * a synchronous response carrying the same token. Both mean the purchase ended in a state that cannot
 * be told apart from one that collected, so both must classify as `failed` and lock buying.
 */
export function ordwayPurchaseFailedError(): OrdwayPurchaseError {
  return new OrdwayPurchaseError(ORDWAY_PURCHASE_FAILURE_MESSAGE, 'failed');
}

/**
 * Shown when the purchase is still running once polling gives up. Polling stops well before the
 * workflow's own deadline, and the workflow orders, bills, and waits for an invoice before charging
 * the card, so at this point the charge may not have been attempted — and may still succeed. The copy
 * therefore asserts neither a payment nor a failure, and points at the balance rather than inviting a
 * retry that could double-charge.
 */
export function ordwayPurchaseStillProcessingMessage(rail: OrdwayPurchaseRail): string {
  return `Your purchase is still processing. Check your ${rail.balanceLabel} in a few minutes.`;
}

/**
 * The synchronous outcome a services build predating the workflow reports, or `undefined` when the
 * response carries no token this client trusts. Only explicit tokens count: the flow this replaces
 * treated every non-`failed` value as a purchase, which is what let a `pending` response read as a
 * completed sale. An unrecognized value stays indeterminate instead.
 */
export function parseLegacyOrdwayPurchaseOutcome(response: unknown): 'success' | 'failed' | undefined {
  const status = get(response, 'status');
  if (status === 'success' || status === 'granted') {
    return 'success';
  }
  return status === 'failed' ? 'failed' : undefined;
}

/** The in-flight purchase a 409 from a purchase endpoint points at. */
export type OrdwayPurchaseConflict = {
  /**
   * The Temporal run id of the purchase already running for this account, when the server supplied
   * one. Absent when it could not name the run — the conflict is still real and nothing was charged
   * for this attempt, there is just nothing to poll.
   */
  runId?: string;
  /**
   * How much the running purchase is for: credit packs on the AI rail, previews on the Inbox Previews
   * one. Present only when the server could read it from the running workflow.
   */
  size?: number;
};

/**
 * The in-flight purchase described by a 409 from an Ordway purchase endpoint, or `undefined` when the
 * error is not a conflict at all. The size lives under a different `meta` key per rail, which is why
 * the rail is read for it rather than both keys being tried.
 *
 * The `meta` is read defensively: the status alone establishes that another purchase is running and
 * that this attempt was refused, so a missing or blank run id narrows what the caller can do rather
 * than making it an ordinary failure.
 */
export function parseOrdwayPurchaseConflict(
  error: unknown,
  rail: OrdwayPurchaseRail,
): OrdwayPurchaseConflict | undefined {
  if (getApiClientErrorStatus(error) !== 409) {
    return undefined;
  }

  const meta = get(getApiClientErrorBody(error), 'errors[0].meta');
  const runId = get(meta, 'run_id');
  const size = get(meta, rail.sizeMetaKey);
  return {
    runId: typeof runId === 'string' && !isEmptyString(runId) ? runId : undefined,
    size: typeof size === 'number' ? size : undefined,
  };
}

/** One status-poll attempt's outcome. `unavailable` is a transient request failure worth retrying. */
export type OrdwayPurchaseStatusRead =
  | { kind: 'status'; status: OrdwayPurchaseStatus }
  | { kind: 'unrecognized'; status: string }
  | { kind: 'not-found' }
  | { kind: 'unavailable' };

/**
 * Reads a purchase workflow's current status for one poll attempt. A rail supplies `fetchStatus`, its
 * own status endpoint bound to the account and run; classifying what comes back is identical for all
 * of them.
 */
export async function readOrdwayPurchaseStatus(
  fetchStatus: () => Promise<{ status: string }>,
): Promise<OrdwayPurchaseStatusRead> {
  try {
    const res = await fetchStatus();
    const status = parseOrdwayPurchaseStatus(res.status);
    return status != null ? { kind: 'status', status } : { kind: 'unrecognized', status: res.status };
  } catch (error) {
    return getApiClientErrorStatus(error) === 404 ? { kind: 'not-found' } : { kind: 'unavailable' };
  }
}

/**
 * The user-facing error for a terminal status, or `undefined` while the purchase is still pending.
 * `success` is handled by the caller, which resolves rather than throwing.
 */
export function ordwayPurchaseStatusError(status: Exclude<OrdwayPurchaseStatus, 'success'>): Error | undefined {
  switch (status) {
    case 'pending':
      return undefined;
    case 'declined':
      return new OrdwayPurchaseError(ORDWAY_PURCHASE_DECLINED_MESSAGE, 'declined');
    case 'failed':
      return ordwayPurchaseFailedError();
    default: {
      const exhaustive: never = status;
      return new Error(String(exhaustive));
    }
  }
}

/**
 * Reports an indeterminate poll outcome to Sentry and returns the error to throw. A run the status
 * endpoint cannot describe, or a status this client does not recognize, says nothing about whether
 * the card was charged — so this reports the same unknown outcome as a timeout rather than the
 * failure copy, which would be a definite claim the poll never established.
 */
export function reportIndeterminateOrdwayPurchase(rail: OrdwayPurchaseRail, diagnostic: string): OrdwayPurchaseError {
  sentryCaptureException(new Error(diagnostic));
  return ordwayPurchaseIndeterminateError(rail);
}

/** The error for an outcome the client never established, paired with the copy that claims nothing. */
export function ordwayPurchaseIndeterminateError(rail: OrdwayPurchaseRail): OrdwayPurchaseError {
  return new OrdwayPurchaseError(ordwayPurchaseStillProcessingMessage(rail), 'indeterminate');
}

/**
 * Whether a purchase POST that threw leaves open that the server started the purchase anyway.
 *
 * The endpoint refuses a bad request — not an Ordway account, no saved card, a non-card default, or
 * an environment with no Ordway product configured — before it creates an order or starts a workflow,
 * so those establish that nothing was charged. A lost response is the opposite: a client timeout or a
 * network failure says nothing about what the server did, and a 5xx can arrive *after* the work began
 * — on the synchronous path a grant failing once the card was charged is exactly a 500. Those must
 * keep buying locked, because the server's duplicate guard lapses once that first purchase ends.
 *
 * A 5xx raised before anything was created (a slow Ordway payment-method lookup answering 503) locks
 * too, which is stricter than it needs to be. That is the deliberate side to err on: an unnecessary
 * lock costs a reopened form, and the customer can still see their balance, while a missed one costs
 * a second charge.
 */
export function ordwayPurchaseMayHaveStarted(error: unknown): boolean {
  const status = getApiClientErrorStatus(error);
  return status === undefined || status >= 500;
}

/** Running state a purchase poll carries across its attempts. */
export type OrdwayPollTracker = {
  /** How many not-found reads have arrived back-to-back. Resets on any read the route answered. */
  readonly notFoundStreak: number;
  /** Folds one attempt's read into the running state. */
  record: (read: OrdwayPurchaseStatusRead) => void;
  /** Diagnostic for a poll that used up `attempts` without reaching a terminal status. */
  timedOutDiagnostic: (attempts: number) => string;
};

/**
 * Bookkeeping a poll keeps across attempts, held here so the loop itself stays a read-and-decide.
 * It owns two rules that only make sense over a whole poll rather than a single read: the
 * consecutive-404 streak, and reporting an unrecognized status token no more than once.
 */
export function createOrdwayPollTracker(rail: OrdwayPurchaseRail, runId: string): OrdwayPollTracker {
  let notFound = 0;
  let lastStatus: OrdwayPurchaseStatus | undefined;
  let reportedUnrecognized = false;

  return {
    get notFoundStreak() {
      return notFound;
    },

    record(read) {
      if (read.kind === 'not-found') {
        notFound += 1;
        return;
      }
      // An `unavailable` read is the request failing, not the route answering, so it neither
      // extends the 404 streak nor proves the route is live — leave the streak untouched.
      if (read.kind === 'unavailable') {
        return;
      }
      // A read the route answered proves it is live, so the tolerance counts consecutive 404s. During
      // a rolling deploy reads alternate between instances, and cumulative counting would exhaust the
      // budget on a healthy run.
      notFound = 0;
      if (read.kind === 'status') {
        lastStatus = read.status;
        return;
      }
      if (!reportedUnrecognized) {
        // Once per poll: the token repeats on every attempt, and the run may still settle normally.
        reportedUnrecognized = true;
        sentryCaptureException(
          new Error(`Ordway ${rail.label} purchase run ${runId} reported an unrecognized status: ${read.status}`),
        );
      }
    },

    timedOutDiagnostic(attempts) {
      return `Ordway ${rail.label} purchase polling timed out after ${attempts} attempts for run ${runId} (lastStatus: ${lastStatus ?? 'none'})`;
    },
  };
}
